import React, { useState, useEffect } from 'react';
import { ShieldCheck, Package, ArrowLeft, CreditCard, Banknote } from 'lucide-react';
import { CartItem, ShippingDetails } from '../types';
import { formatPrice, loadRazorpay } from '../utils';
import { auth } from '../firebase';

interface OrderSuccessData {
  orderId: string;
  amount: number;
  items: { name: string; quantity: number }[];
}

interface CheckoutPageProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onSuccess: (data: OrderSuccessData) => void;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

type PaymentMethod = 'prepaid' | 'cod';

const CheckoutPage: React.FC<CheckoutPageProps> = ({ isOpen, onClose, cartItems, onSuccess }) => {
  const [details, setDetails] = useState<ShippingDetails>({ 
    name: '', email: '', address: '', city: '', state: '', pincode: '', phone: '' 
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('prepaid');

  // Preload the Razorpay SDK as soon as the checkout modal opens
  // so the user doesn't wait for it to download when they click the button
  useEffect(() => {
    if (isOpen && paymentMethod === 'prepaid') {
      loadRazorpay();
    }
  }, [isOpen, paymentMethod]);

  const total = cartItems.reduce((sum, item) => sum + parseFloat(item.price) * item.quantity, 0);
  const shippingCost = total < 399 ? 69 : 0;
  const codSurcharge = paymentMethod === 'cod' ? Math.round(total * 0.15) : 0;
  const finalTotal = total + shippingCost + codSurcharge;

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Step 1: Check if user is logged in
    const user = auth.currentUser;
    if (!user) {
      setError('Please login before placing an order.');
      setLoading(false);
      return;
    }

    try {
      // Step 2: Get Firebase auth token
      const idToken = await user.getIdToken();

      if (paymentMethod === 'cod') {
        // ─── COD Flow ───
        const response = await fetch(`${API_URL}/api/create-cod-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            cartItems: cartItems.map(item => ({ id: item.id, quantity: item.quantity })),
            shippingDetails: details,
          }),
        });

        const orderData = await response.json();

        if (!response.ok) {
          setError(orderData.error || 'Failed to create order. Please try again.');
          setLoading(false);
          return;
        }

        // COD order placed successfully
        onSuccess({
          orderId: orderData.formattedOrderId,
          amount: orderData.amount,
          items: cartItems.map(item => ({ name: item.name, quantity: item.quantity })),
        });
      } else {
        // ─── Prepaid (Razorpay) Flow ───
        const sdkLoaded = await loadRazorpay();
        if (!sdkLoaded) {
          setError('Payment gateway failed to load. Please refresh and try again.');
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/api/create-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            cartItems: cartItems.map(item => ({ id: item.id, quantity: item.quantity })),
            shippingDetails: details,
          }),
        });

        const orderData = await response.json();

        if (!response.ok) {
          setError(orderData.error || 'Failed to create order. Please try again.');
          setLoading(false);
          return;
        }

        // Open Razorpay checkout with the secure order from backend
        const options = {
          key: import.meta.env.VITE_RAZORPAY_KEY_ID,
          amount: orderData.amount,
          currency: orderData.currency,
          name: "YUMECAV",
          description: "Order Payment",
          order_id: orderData.orderId,
          handler: async function (response: any) {
            try {
              const verifyRes = await fetch(`${API_URL}/api/verify-payment`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${idToken}`,
                },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  dbOrderId: orderData.dbOrderId,
                }),
              });

              const verifyData = await verifyRes.json();
              if (verifyData.success) {
                onSuccess({
                  orderId: verifyData.formattedOrderId || response.razorpay_order_id,
                  amount: orderData.amount,
                  items: cartItems.map(item => ({ name: item.name, quantity: item.quantity })),
                });
              } else {
                setError('Payment verification failed. Contact support.');
              }
            } catch {
              setError('Payment verification failed. Contact support.');
            }
          },
          prefill: {
            name: details.name,
            email: details.email,
            contact: details.phone,
          },
          theme: { color: "#a855f7" },
          modal: {
            ondismiss: () => {
              setLoading(false);
            },
          },
        };

        const rzp = new (window as any).Razorpay(options);
        rzp.on('payment.failed', () => {
          setError('Payment failed. Please try again.');
          setLoading(false);
        });
        rzp.open();
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black overflow-y-auto animate-fade-in no-scrollbar p-6">
      <div className="max-w-5xl mx-auto py-12">
        <header className="flex justify-between items-center mb-16">
          <button onClick={onClose} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-neutral-500 hover:text-white">
            <ArrowLeft className="w-3 h-3" /> Back_to_Archive
          </button>
          <div className="text-xl font-black italic">YUME<span className="text-amber-500">CAV</span>_CHECKOUT</div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <form onSubmit={handlePayment} className="space-y-6">
            <div className="flex items-center gap-4 mb-8">
               <Package className="w-6 h-6 text-amber-500" />
               <h2 className="text-3xl font-black uppercase tracking-tighter">Shipping Logistics</h2>
            </div>
            
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-xs font-bold uppercase tracking-wide">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <input required placeholder="FULL NAME" className="w-full bg-neutral-900 border border-white/10 rounded-xl p-4 text-xs font-black tracking-widest uppercase focus:border-amber-500 outline-none" value={details.name} onChange={e => setDetails({...details, name: e.target.value})} />
              <input required type="email" placeholder="EMAIL" className="w-full bg-neutral-900 border border-white/10 rounded-xl p-4 text-xs font-black tracking-widest uppercase focus:border-amber-500 outline-none" value={details.email} onChange={e => setDetails({...details, email: e.target.value})} />
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="PHONE" className="w-full bg-neutral-900 border border-white/10 rounded-xl p-4 text-xs font-black tracking-widest uppercase focus:border-amber-500 outline-none" value={details.phone} onChange={e => setDetails({...details, phone: e.target.value})} />
                <input required placeholder="PINCODE" className="w-full bg-neutral-900 border border-white/10 rounded-xl p-4 text-xs font-black tracking-widest uppercase focus:border-amber-500 outline-none" value={details.pincode} onChange={e => setDetails({...details, pincode: e.target.value})} />
              </div>
              <textarea required placeholder="COMPLETE ADDRESS" className="w-full bg-neutral-900 border border-white/10 rounded-xl p-4 text-xs font-black tracking-widest uppercase focus:border-amber-500 outline-none min-h-[120px]" value={details.address} onChange={e => setDetails({...details, address: e.target.value})} />
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="CITY" className="w-full bg-neutral-900 border border-white/10 rounded-xl p-4 text-xs font-black tracking-widest uppercase focus:border-amber-500 outline-none" value={details.city} onChange={e => setDetails({...details, city: e.target.value})} />
                <input required placeholder="STATE" className="w-full bg-neutral-900 border border-white/10 rounded-xl p-4 text-xs font-black tracking-widest uppercase focus:border-amber-500 outline-none" value={details.state} onChange={e => setDetails({...details, state: e.target.value})} />
              </div>
            </div>

            {/* ─── Payment Method Selector ─── */}
            <div className="pt-2">
              <h3 className="text-xs font-black uppercase tracking-widest text-neutral-500 mb-4 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Payment Method
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('prepaid')}
                  className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                    paymentMethod === 'prepaid'
                      ? 'border-amber-500 bg-amber-500/10 shadow-[0_0_20px_rgba(168,85,247,0.15)]'
                      : 'border-white/10 bg-neutral-900 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <CreditCard className={`w-5 h-5 ${paymentMethod === 'prepaid' ? 'text-amber-400' : 'text-neutral-500'}`} />
                    <span className="text-xs font-black uppercase tracking-widest">Online</span>
                  </div>
                  <p className="text-[10px] text-neutral-400 leading-relaxed">
                    UPI, Cards, Net Banking
                  </p>
                  {paymentMethod === 'prepaid' && (
                    <div className="absolute top-3 right-3 w-3 h-3 bg-amber-500 rounded-full animate-pulse"></div>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMethod('cod')}
                  className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                    paymentMethod === 'cod'
                      ? 'border-amber-500 bg-amber-500/10 shadow-[0_0_20px_rgba(245,158,11,0.15)]'
                      : 'border-white/10 bg-neutral-900 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Banknote className={`w-5 h-5 ${paymentMethod === 'cod' ? 'text-amber-400' : 'text-neutral-500'}`} />
                    <span className="text-xs font-black uppercase tracking-widest">COD</span>
                  </div>
                  <p className="text-[10px] text-neutral-400 leading-relaxed">
                    Cash on Delivery (+15%)
                  </p>
                  {paymentMethod === 'cod' && (
                    <div className="absolute top-3 right-3 w-3 h-3 bg-amber-500 rounded-full animate-pulse"></div>
                  )}
                </button>
              </div>

              {/* COD info banner */}
              {paymentMethod === 'cod' && (
                <div className="mt-3 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex items-start gap-2">
                  <Banknote className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-amber-300/80 leading-relaxed">
                    A <span className="font-black text-amber-300">15% COD surcharge</span> ({formatPrice(codSurcharge)}) will be added to your order. 
                    Pay in cash when the package arrives at your doorstep.
                  </p>
                </div>
              )}
            </div>

            <button type="submit" disabled={loading} className={`w-full py-6 rounded-2xl font-black uppercase tracking-[0.2em] transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
              paymentMethod === 'cod' 
                ? 'bg-amber-500 text-black hover:bg-amber-400' 
                : 'bg-white text-black hover:bg-amber-600 hover:text-white'
            }`}>
              {loading 
                ? 'PROCESSING...' 
                : paymentMethod === 'cod'
                  ? `Place COD Order (${formatPrice(finalTotal)})`
                  : `Proceed to Payment (${formatPrice(finalTotal)})`
              }
            </button>
          </form>

          <div className="bg-neutral-900/50 rounded-3xl p-8 border border-white/5 h-fit">
            <h3 className="text-xs font-black uppercase tracking-widest text-neutral-500 mb-6 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Order_Summary
            </h3>
            <div className="space-y-4 mb-8">
              {cartItems.map(item => (
                <div key={item.id} className="flex justify-between items-center text-[10px] font-black uppercase">
                  <span className="text-neutral-400">{item.name} x{item.quantity}</span>
                  <span>{formatPrice(parseFloat(item.price) * item.quantity)}</span>
                </div>
              ))}
              <div className="flex justify-between items-center text-[10px] font-black uppercase border-t border-white/5 pt-4">
                 <span className="text-neutral-400">Shipping</span>
                 <span>{shippingCost === 0 ? 'FREE' : formatPrice(shippingCost)}</span>
              </div>
              {paymentMethod === 'cod' && (
                <div className="flex justify-between items-center text-[10px] font-black uppercase">
                   <span className="text-amber-400">COD Surcharge (15%)</span>
                   <span className="text-amber-400">+{formatPrice(codSurcharge)}</span>
                </div>
              )}
            </div>
            <div className="border-t border-white/10 pt-6 flex justify-between items-end">
               <span className="text-xs font-black uppercase tracking-widest">Total Manifest</span>
               <span className="text-3xl font-black tracking-tighter text-amber-500">{formatPrice(finalTotal)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;